// Enhanced DevicePermissionModal.tsx - Cross-browser compatible with Safari fixes
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  makeStyles,
  useTheme,
} from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  modal: {
    zIndex: 1500,
    '& .MuiDialog-paper': {
      borderRadius: 16,
      padding: '8px 0',
      minWidth: 400,
      maxWidth: 500,
      margin: 16,
      backgroundColor: theme.palette.background.paper || '#ffffff',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.24)',
      [theme.breakpoints.down('sm')]: {
        minWidth: 300,
        maxWidth: 'calc(100vw - 32px)',
        margin: 16,
      },
    },
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
  },
  header: {
    padding: '24px 24px 16px',
    textAlign: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: 500,
    color: theme.palette.text.primary || '#202124',
    marginBottom: 8,
    lineHeight: 1.3,
  },
  content: {
    padding: '0 24px 24px',
    textAlign: 'center',
  },
  instructionsList: {
    textAlign: 'left',
    marginTop: 16,
    paddingLeft: 0,
    listStyle: 'none',
    '& li': {
      marginBottom: 12,
      color: theme.palette.text.secondary || '#5f6368',
      fontSize: 14,
      lineHeight: 1.4,
      position: 'relative',
      paddingLeft: 24,
      '&:before': {
        content: 'counter(step-counter)',
        counterIncrement: 'step-counter',
        position: 'absolute',
        left: 0,
        top: 0,
        backgroundColor: theme.palette.primary.main || '#1a73e8',
        color: '#ffffff',
        width: 18,
        height: 18,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 'bold',
      },
    },
    counterReset: 'step-counter',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 16,
  },
  deviceIcon: {
    width: 80,
    height: 80,
    backgroundColor: theme.palette.primary.light || '#e8f0fe',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 32,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  actions: {
    padding: '0 24px 16px',
    justifyContent: 'center',
  },
  primaryButton: {
    backgroundColor: theme.palette.primary.main || '#1a73e8',
    color: '#ffffff',
    padding: '10px 24px',
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 120,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark || '#1557b0',
    },
  },
  secondaryButton: {
    color: theme.palette.primary.main || '#1a73e8',
    padding: '10px 16px',
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 500,
    marginRight: 8,
  },
  browserSpecificNote: {
    marginTop: 16,
    padding: 12,
    backgroundColor: theme.palette.grey[50] || '#f8f9fa',
    borderRadius: 8,
    border: `1px solid ${theme.palette.grey[200] || '#e8eaed'}`,
  },
  safariNote: {
    marginTop: 16,
    padding: 12,
    backgroundColor: '#fff3e0',
    borderRadius: 8,
    border: '1px solid #ffcc02',
    '& .MuiTypography-root': {
      color: '#e65100',
      fontSize: 13,
      fontWeight: 500,
    },
  },
}));

interface DevicePermissionModalProps {
  open: boolean;
  onClose: () => void;
  deviceType: 'camera' | 'microphone';
  browser: string;
}

export function DevicePermissionModal({ open, onClose, deviceType, browser }: DevicePermissionModalProps) {
  const classes = useStyles();
  const theme = useTheme();

  const deviceName = deviceType === 'camera' ? 'Camera' : 'Microphone';
  const deviceEmoji = deviceType === 'camera' ? '📷' : '🎤';

  const getBrowserInstructions = () => {
    switch (browser) {
      case 'Chrome':
        return [
          `Look for the ${deviceType} icon in your address bar (next to the URL)`,
          'Click the icon and select "Allow" for this site',
          'If no icon appears, click the lock icon and manage permissions',
          'Refresh the page after changing permissions',
        ];

      case 'Firefox':
        return [
          `Look for the ${deviceType} icon in your address bar`,
          'Click the icon and select "Allow" for this site',
          'You may see a dropdown menu - choose "Allow" and "Remember this decision"',
          'Refresh the page to apply changes',
        ];

      case 'Safari':
        return [
          'Click "Safari" in the top menu, then "Settings for This Website"',
          `Find "${deviceName}" and change it to "Allow"`,
          'Alternatively: Safari > Preferences > Websites > Camera/Microphone',
          'Set this website to "Allow" and refresh the page',
        ];

      case 'Edge':
        return [
          `Click the ${deviceType} icon in your address bar`,
          'Select "Allow" for this site',
          'If no icon appears, click the lock icon and check site permissions',
          'Refresh the page after making changes',
        ];

      case 'Opera':
        return [
          `Look for the ${deviceType} icon in your address bar`,
          'Click it and select "Allow" for this site',
          'Or go to Settings > Advanced > Privacy & Security > Site Settings',
          'Find this website and update permissions',
        ];

      case 'Brave':
        return [
          'Click the Brave shield icon in your address bar',
          `Look for ${deviceName} controls and set to "Allow"`,
          'Or click the lock icon to manage site permissions',
          'Refresh the page to apply changes',
        ];

      case 'Vivaldi':
        return [
          `Click the ${deviceType} icon in your address bar`,
          'Select "Allow" for this site',
          'Or go to Settings > Privacy > Site Settings to manage permissions',
          'Refresh the page after changing settings',
        ];

      default:
        return [
          `Look for a ${deviceType} or lock icon in your address bar`,
          'Click it and look for camera/microphone permissions',
          'Set permissions to "Allow" for this website',
          'Refresh the page to apply the changes',
        ];
    }
  };

  const getAdditionalHelp = () => {
    switch (browser) {
      case 'Chrome':
        return `You can also go to chrome://settings/content/${deviceType} to manage global ${deviceType} settings.`;

      case 'Firefox':
        return 'You can manage all site permissions in Firefox Preferences > Privacy & Security > Permissions.';

      case 'Safari':
        return 'Make sure "Ask websites not to track me" is not interfering with permissions in Safari > Preferences > Privacy.';

      case 'Edge':
        return `Visit edge://settings/content/${deviceType} to manage global ${deviceType} permissions.`;

      default:
        return `Check your browser's privacy or security settings to manage ${deviceType} permissions.`;
    }
  };

  const handleTryAgain = () => {
    // Close modal and trigger a page refresh to re-test permissions
    onClose();
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.modal}
      aria-labelledby="device-permission-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={classes.header} id="device-permission-title">
        <div className={classes.iconContainer}>
          <div className={classes.deviceIcon}>{deviceEmoji}</div>
        </div>
        <Typography className={classes.title}>
          Enable {deviceName} Access in {browser}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          To use your {deviceType} in this meeting, please follow these steps:
        </Typography>

        <ol className={classes.instructionsList}>
          {getBrowserInstructions().map((instruction, index) => (
            <li key={index}>
              <Typography variant="body2">{instruction}</Typography>
            </li>
          ))}
        </ol>

        {/* Safari-specific additional help */}
        {browser === 'Safari' && (
          <div className={classes.safariNote}>
            <Typography variant="body2">
              <strong>Safari Tip:</strong> If you don't see camera/microphone options in Settings, try visiting a
              website that requests camera access first, then return to manage permissions.
            </Typography>
          </div>
        )}

        {/* General additional help */}
        <Box className={classes.browserSpecificNote}>
          <Typography variant="body2" color="textSecondary">
            <strong>Need more help?</strong> {getAdditionalHelp()}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={onClose} className={classes.secondaryButton}>
          Cancel
        </Button>
        <Button onClick={handleTryAgain} className={classes.primaryButton} variant="contained">
          I've Updated Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
}
